"use client";

import { useCurrentLocale, useI18n } from "~/locales/client";
import { IncotermLocationOption } from "~/types-and-enums/incotermLocationTypes";
import { getCountryDisplayName, hasPostalCode } from "~/utils/incotermLocationUtils";

type IncotermLocationOptionContentProps = {
  option: IncotermLocationOption;
};

/**
 * Two-line option content: "{postal_code} {city}" and "{street} • {country} ({country_code})".
 * Options without a postal code get a hint, as they can't be selected.
 */
const IncotermLocationOptionContent = ({ option }: IncotermLocationOptionContentProps) => {
  const translate = useI18n();
  const locale = useCurrentLocale();

  const localisedCountry = getCountryDisplayName(option.country_code, locale);
  const countryName =
    localisedCountry === option.country_code ? (option.country ?? localisedCountry) : localisedCountry;
  const details = [option.street, `${countryName} (${option.country_code})`].filter(Boolean).join(" • ");

  return (
    <>
      <span className="block text-sm font-medium text-gray-900 whitespace-normal">
        {[option.postal_code, option.city].filter(Boolean).join(" ")}
      </span>
      <span className="block mt-1 text-xs text-gray-500 whitespace-normal">
        {details}
        {!hasPostalCode(option) && (
          <span className="text-red-600">
            {" • "}
            {translate("incoterm-location.missing-postal-code")}
          </span>
        )}
      </span>
    </>
  );
};

export default IncotermLocationOptionContent;
